import './index.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import {
  makeChangeSubmissionsType,
  makeDeleteSubmissions,
  makeReassignSubmissionsGroup,
} from '@actions/submissionActions';
import {
  setIsOpenChangeTypeModal,
  setIsOpenMultipleDeleteModal,
  setIsOpenReassignGroupModal,
  setPartialFilters,
} from '@actions/lettersPageActions';
import { MgState, SubmissionQueryInterface, SubmissionState } from '@types';
import { SearchTextField } from '@components/Pages/LettersPage/Filters/Elements/SearchTextField';
import { translate } from '@utils';
import { Badge, Button, IconButton, Popover, Tooltip } from '@material-ui/core';
import { EditFolderIcon, FilterListOffIcon, TransformIcon, TrashIcon } from '@images/icons';
import {
  ChangeSubmissionsTypeModal,
  DeleteModal,
  ReassignSubmissionsGroupModal,
} from '@components/Modals';
import {
  canRemoveSubmissionsSelector,
  canTransferCommonSubmissionsSelector,
  canTransferOwnSubmissionsSelector,
  canUpdateSubmissionsSelector,
  canViewPrivateSubmissionsSelector,
  submissionLengthSelector,
} from '@selectors/submission';
import { SubmissionTypes } from '@enums';
import { useParams } from 'react-router-dom';
import { GroupsSelect } from '@components/Pages/LettersPage/Filters/Elements/GroupsSelect';
import {
  lettersFiltersHasChanged,
  lettersPageFiltersCompaniesSelector,
  lettersPageFiltersLanguageSelector,
  lettersPageFiltersSubmissionTypeSelector,
  lettersPageSelector,
} from '@selectors/lettersPage';
import { LettersFilterLabel } from '@components/Pages/LettersPage/Filters/Elements/LettersFilterLabel';
import { initialFilters } from '@reducers/initialState/lettersPage';
import { FilterPopover } from '@sharedComponents/FilterPopover';

// TODO: Refactor the component
export const FiltersContainer = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const { id } = useParams();

  const { total } = useSelector<MgState, SubmissionState>(({ submission }) => submission);
  const checkedSubmissions = useSelector<MgState, string[]>(
    ({ submission }) => submission.checkedSubmissions,
    shallowEqual
  );

  const {
    isOpenMultipleDeleteModal: isOpenDeleteModal,
    isOpenReassignGroupModal,
    isOpenChangeTypeModal,
  } = useSelector(lettersPageSelector);
  const submissionType =
    useSelector(lettersPageFiltersSubmissionTypeSelector) || SubmissionTypes.All;

  const submissionLanguage = useSelector(lettersPageFiltersLanguageSelector);

  const submissionCompanies = useSelector(lettersPageFiltersCompaniesSelector);

  const isSubmissionsDeleteLoading = useSelector<MgState, boolean>(
    ({ submission }) => submission.isSubmissionsDeleteLoading
  );
  const canTransferOwnSubmissions = useSelector(canTransferOwnSubmissionsSelector);
  const canTransferCommonSubmissions = useSelector(canTransferCommonSubmissionsSelector);
  const canRemoveSubmissions = useSelector(canRemoveSubmissionsSelector);
  const canUpdateSubmissions = useSelector(canUpdateSubmissionsSelector);
  const filtersHasChanged = useSelector(lettersFiltersHasChanged);
  const canViewPrivateSubmissions = useSelector(canViewPrivateSubmissionsSelector);

  const showResultOfSearch = useSelector<MgState, boolean>(
    ({
      lettersPage: {
        filters: { title, groupId, submissionType },
      },
    }) => Boolean(title) || Boolean(groupId) || submissionType !== SubmissionTypes.All
  );
  const submissionsLength = useSelector<MgState, number>(submissionLengthSelector);

  useEffect(() => {
    dispatch(setPartialFilters({}, false, !id));
  }, []);

  const handleDeleteSubmissions = useCallback(() => {
    dispatch(makeDeleteSubmissions({ submissionIds: checkedSubmissions }));
  }, [checkedSubmissions]);

  const handleReassignGroup = useCallback(
    (groupId: string) => {
      dispatch(makeReassignSubmissionsGroup({ groupId, submissionIds: checkedSubmissions }));
    },
    [checkedSubmissions]
  );

  const handleChangeSubmissionsType = useCallback(() => {
    dispatch(makeChangeSubmissionsType({ submissionIds: checkedSubmissions, isCommon: true }));
  }, [checkedSubmissions]);

  const handleFilterChange = (value: Partial<SubmissionQueryInterface>) => {
    dispatch(setPartialFilters(value));
    setAnchorEl(null);
  };

  const handleLettersFilterLabelClick = (event) => {
    setAnchorEl(event.target);
  };

  const openFilterPopover = Boolean(anchorEl);

  const clearFiltersComponent = (
    <Tooltip title={translate('clear_filters')} arrow>
      <IconButton
        onClick={() => handleFilterChange(initialFilters)}
        size="small"
        color="primary"
        aria-label="filtering"
        component="div"
        data-testid="clear-letters-filters"
      >
        <FilterListOffIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <div className="filters-container">
      <div className="filters-inputs-top">
        <SearchTextField />
        <GroupsSelect />
      </div>
      {showResultOfSearch && (
        <div className="search-result">
          <span className="search-result__text">{translate('search_results')}: </span>
          <span>
            {translate('matches')} {submissionsLength} {translate('of')} {total}
          </span>
        </div>
      )}
      <div className="actions-container">
        <div>{filtersHasChanged && clearFiltersComponent}</div>
        <div>
          <div className="actions-container__buttons">
            {canTransferCommonSubmissions && (
              <Tooltip
                title={
                  checkedSubmissions.length > 1
                    ? translate('reassign_several_group')
                    : translate('reassign_one_group')
                }
                arrow
              >
                <span className="letters-list-button">
                  <Button
                    onClick={() => dispatch(setIsOpenReassignGroupModal(true))}
                    disabled={_isEmpty(checkedSubmissions)}
                    disableRipple
                  >
                    <EditFolderIcon className="edit_folder_icon" />
                  </Button>
                </span>
              </Tooltip>
            )}
            {/*TODO: Refactoring required*/}
            {submissionType === SubmissionTypes.Own && (
              <>
                {canTransferOwnSubmissions && (
                  <Tooltip
                    title={
                      checkedSubmissions.length > 1
                        ? translate('reassign_several_group')
                        : translate('reassign_one_group')
                    }
                    arrow
                  >
                    <span className="letters-list-button">
                      <Button
                        onClick={() => dispatch(setIsOpenReassignGroupModal(true))}
                        disabled={_isEmpty(checkedSubmissions)}
                        disableRipple
                      >
                        <EditFolderIcon className="edit_folder_icon" />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {canUpdateSubmissions && (
                  <Tooltip
                    title={
                      checkedSubmissions.length > 1
                        ? translate('reassign_several')
                        : translate('reassign_one')
                    }
                    arrow
                  >
                    <span className="letters-list-button">
                      <Button
                        onClick={() => dispatch(setIsOpenChangeTypeModal(true))}
                        disabled={_isEmpty(checkedSubmissions)}
                        disableRipple
                      >
                        <TransformIcon />
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {canRemoveSubmissions && (
                  <Tooltip title={translate('delete')} arrow>
                    <span className="letters-list-button">
                      <Button
                        onClick={() => dispatch(setIsOpenMultipleDeleteModal(true))}
                        disabled={_isEmpty(checkedSubmissions)}
                        disableRipple
                      >
                        <TrashIcon />
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
            <div className="filters-label">
              <Badge
                color="secondary"
                overlap="circular"
                variant="dot"
                invisible={!filtersHasChanged}
              >
                <LettersFilterLabel handleFiltersClick={handleLettersFilterLabelClick} />
              </Badge>
              <Popover
                open={openFilterPopover}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <FilterPopover
                  handleFilterChange={handleFilterChange}
                  submissionType={submissionType}
                  filterCompanies={submissionCompanies}
                  filterLanguage={submissionLanguage}
                  canViewPrivateTemplate={canViewPrivateSubmissions}
                />
              </Popover>
            </div>
          </div>
          <ReassignSubmissionsGroupModal
            title={
              checkedSubmissions.length > 1
                ? translate('move_templates')
                : translate('move_template')
            }
            open={isOpenReassignGroupModal}
            handleCancel={() => dispatch(setIsOpenReassignGroupModal(false))}
            handleConfirmation={handleReassignGroup}
          />
          <ChangeSubmissionsTypeModal
            title={
              checkedSubmissions.length > 1
                ? translate('reassign_question_multiple')
                : translate('reassign_question')
            }
            handleConfirmation={handleChangeSubmissionsType}
            open={isOpenChangeTypeModal}
            handleCancel={() => dispatch(setIsOpenChangeTypeModal(false))}
            contentName="caution_message"
          />
          <DeleteModal
            title={
              checkedSubmissions.length > 1
                ? translate('delete_question_multiple')
                : translate('delete_question')
            }
            handleConfirmation={handleDeleteSubmissions}
            isDisabledConfirmationButton={isSubmissionsDeleteLoading}
            open={isOpenDeleteModal}
            handleCancel={() => dispatch(setIsOpenMultipleDeleteModal(false))}
            contentName="caution_message"
          />
        </div>
      </div>
    </div>
  );
};
